import React, { forwardRef, useImperativeHandle, useState } from "react";
import { Table } from "antd";
import { RenderClearFiltersButton } from "shared/ui";
import {
  MachineItem,
  TaskExecution,
  useGetTasksExecutionQuery,
  UserItem,
} from "shared/api";
import {
  useDateFilters,
  useDebouncedTableFilter,
  usePagination,
} from "shared/hooks";
import { useGetTaskExecutionsColumns } from "../../../../../shared/hooks/useGetTaskExecutionsColumns";
import { StatusEditModal } from "./StatusEditModal";
import { UserEditModal } from "./UserEditModal";

interface Props {
  transformedUsers: {
    label: string;
    value: number | null | undefined;
  }[];
  allUsers: UserItem[];
  machinesData: MachineItem[] | undefined;
}

const TaskScheduleTable = forwardRef(
  ({ transformedUsers, allUsers, machinesData }: Props, ref) => {
    const [selectedUserName, setSelectedUserName] = useState<string | null>(
      null
    );
    const pagination = usePagination(10);
    const { setCurrentPage, currentPage, pageSize, setPageSize } = pagination;

    const [priorityValue, setPriorityValue] = useState<number | null>(null);

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedExec, setSelectedExec] = useState<TaskExecution | null>(
      null
    );
    const [selectedMachines, setSelectedMachines] = useState<number[]>([]);
    const [statusValues, setStatusValues] = useState<string[]>([]);

    const openStatusModal = (rec: TaskExecution) => {
      setSelectedExec(rec);
      setModalOpen(true);
    };

    const [userModalOpen, setUserModalOpen] = useState(false);
    const [selectedUserRecord, setSelectedUserRecord] =
      useState<TaskExecution | null>(null);

    const openUserModal = (rec: TaskExecution) => {
      setSelectedUserRecord(rec);
      setUserModalOpen(true);
    };

    const handlePriorityValue = (value: number | null) => {
      setPriorityValue(value);
    };
    const clearPriorityValue = () => {
      setPriorityValue(null);
    };

    const {
      debouncedValue: debouncedTaskNameValue,
      handleFilterChange: handleTaskNameValue,
      value: taskNameValue,
      clearFilterValue: clearTaskNameValue,
    } = useDebouncedTableFilter("taskExecutionName", 400);

    const {
      debouncedValue: debouncedTaskMessageValue,
      handleFilterChange: handleTaskMessageValue,
      value: taskMessageValue,
      clearFilterValue: clearTaskMessageValue,
    } = useDebouncedTableFilter("taskExecutionMessageName", 400);

    const {
      dateGreater: expectedDateGreater,
      dateLesser: expectedDateLesser,
      clearDates: clearExpectedDates,
      handleDateGreater: handleExpectedDateGreater,
      handleDateLesser: handleExpectedDateLesser,
      isDataFilterActive: isExpectedFilterActive,
    } = useDateFilters();

    const {
      dateGreater: executedDateGreater,
      dateLesser: executedDateLesser,
      clearDates: clearExecutedDates,
      handleDateGreater: handleExecutedDateGreater,
      handleDateLesser: handleExecutedDateLesser,
      isDataFilterActive: isExecutedFilterActive,
    } = useDateFilters();

    const { data: executionTasks, refetch: refetchExecutions } =
      useGetTasksExecutionQuery({
        task__name: debouncedTaskNameValue || "",
        timestamp_expected__gte: expectedDateGreater ?? undefined,
        timestamp_expected__lte: expectedDateLesser ?? undefined,
        timestamp_executed__gte: executedDateGreater ?? undefined,
        timestamp_executed__lte: executedDateLesser ?? undefined,
        status__in: statusValues.length ? statusValues : undefined,
        message: debouncedTaskMessageValue || "",
        priority: priorityValue ?? undefined,
        user__username: selectedUserName ?? undefined,
        task__machines__machine__in:
          selectedMachines.length > 0 ? selectedMachines : undefined,
        expand: "task.machines",
      });

    useImperativeHandle(ref, () => ({
      refetchExecutions,
    }));

    const taskExecutionsColumns = useGetTaskExecutionsColumns({
      transformedUsers,
      // Task Name
      handleTaskNameValue,
      taskNameValue,
      // Expected Time
      handleExpectedDateGreater,
      expectedDateGreater,
      handleExpectedDateLesser,
      expectedDateLesser,
      isExpectedFilterActive,
      // Executed Time
      handleExecutedDateGreater,
      handleExecutedDateLesser,
      executedDateGreater,
      executedDateLesser,
      isExecutedFilterActive,
      // Status
      statusValues,
      setStatusValues,
      // Message
      handleTaskMessageValue,
      taskMessageValue,
      // Priority
      handlePriorityValue,
      priorityValue,
      // User
      selectedUserName,
      setSelectedUserName,
      // Changes status and user
      openStatusModal,
      openUserModal,
      // Machines
      selectedMachines,
      setSelectedMachines,
      machinesData,
    });

    const isVisibleClearFiltersButton =
      !!taskNameValue ||
      !!expectedDateGreater ||
      !!expectedDateLesser ||
      !!executedDateGreater ||
      !!executedDateLesser ||
      statusValues.length > 0 ||
      !!taskMessageValue ||
      !!priorityValue ||
      !!selectedUserName ||
      selectedMachines.length > 0;

    const clearAllFilters = () => {
      clearTaskNameValue();
      clearExpectedDates();
      clearExecutedDates();
      clearTaskMessageValue();
      clearPriorityValue();
      setSelectedUserName("");
      setSelectedMachines([]);
      setStatusValues([]);
    };

    return (
      <>
        <div className="mt-4">
          {isVisibleClearFiltersButton && (
            <RenderClearFiltersButton onClick={clearAllFilters} />
          )}
          <Table
            dataSource={executionTasks}
            columns={taskExecutionsColumns}
            pagination={{
              pageSizeOptions: ["5", "10", "20", "50"],
              current: currentPage,
              pageSize,
              total: executionTasks?.length || 0,
              showSizeChanger: true,
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              },
            }}
            rowKey="key"
            scroll={{ x: true }}
          />
        </div>
        <StatusEditModal
          open={modalOpen}
          record={selectedExec}
          onClose={() => setModalOpen(false)}
        />
        <UserEditModal
          open={userModalOpen}
          record={selectedUserRecord}
          users={allUsers}
          onClose={() => setUserModalOpen(false)}
        />
      </>
    );
  }
);

TaskScheduleTable.displayName = "TaskScheduleTable";

export default TaskScheduleTable;
